/**
 * Builds POST /api/v1/Conversation/Initiate bodies that match the backend contract.
 * Omits null/invalid optional fields — explicit nulls often produce 400 responses.
 */
import type { InitiateConversationRequest } from './conversationTypes';

export interface BuildInitiatePayloadOptions {
  /** Drop the logged-in user if they appear in participantUserIds by mistake. */
  excludeUserId?: number | null;
}

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

/** Normalizes participant ids to positive integers. */
export const sanitizeParticipantUserIds = (
  ids: Array<number | string | null | undefined>,
  options?: BuildInitiatePayloadOptions,
): number[] => {
  const exclude =
    options?.excludeUserId != null && Number.isFinite(Number(options.excludeUserId))
      ? Number(options.excludeUserId)
      : null;

  const seen = new Set<number>();
  const result: number[] = [];

  for (const raw of ids) {
    const id = Number(raw);
    if (!Number.isFinite(id) || id <= 0) continue;
    if (exclude != null && id === exclude) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    result.push(id);
  }

  return result;
};

/**
 * Returns a payload safe to POST to Conversation/Initiate.
 * - participantUserIds: positive integers only (never NaN/null slots)
 * - optional strings omitted when empty/null
 * - includeAiAgent + aiAgentUserId only when agent id is valid
 */
export const buildInitiateConversationPayload = (
  input: InitiateConversationRequest,
  options?: BuildInitiatePayloadOptions,
): InitiateConversationRequest => {
  const participantUserIds = sanitizeParticipantUserIds(
    input.participantUserIds ?? [],
    options,
  );

  const payload: InitiateConversationRequest = {
    participantUserIds,
  };

  if (isNonEmptyString(input.conversationType)) {
    payload.conversationType = input.conversationType.trim();
  }

  if (isNonEmptyString(input.conversationName)) {
    payload.conversationName = input.conversationName.trim();
  }

  if (isNonEmptyString(input.avatarUrl)) {
    payload.avatarUrl = input.avatarUrl.trim();
  }

  if (input.includeAiAgent === true) {
    payload.includeAiAgent = true;
    const agentId = Number(input.aiAgentUserId);
    if (Number.isFinite(agentId) && agentId > 0) {
      payload.aiAgentUserId = agentId;
    }
  }

  return payload;
};
